import { Icon } from '@iconify/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  buildRaspadinhaPublicPageStyleVars,
  fetchRaspadinhaPublicCampaign,
  RaspadinhaPublicCampaignUnavailableError,
} from '@/lib/raspadinha-public'
import type { RaspadinhaPublicCampaign } from '@/types/raspadinha-public-campaign'
import { RaspadinhaPublicPixels } from './RaspadinhaPublicPixels'
import { RaspadinhaPublicReceiptStep } from './RaspadinhaPublicReceiptStep'
import { RaspadinhaPublicRegisterStep } from './RaspadinhaPublicRegisterStep'
import { RaspadinhaPublicScratchStep } from './RaspadinhaPublicScratchStep'
import { RaspadinhaPublicWelcomeStep } from './RaspadinhaPublicWelcomeStep'
import styles from './RaspadinhaPublicPage.module.css'

type RaspadinhaPublicStep = 'welcome' | 'register' | 'receipt' | 'experience'

function pickPageTitle(campaign: RaspadinhaPublicCampaign): string {
  const title = campaign.title.trim()
  if (title) return title
  return campaign.name.trim() || 'Campanha raspadinha'
}

export function RaspadinhaPublicPage() {
  const { slug } = useParams<{ slug: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [campaign, setCampaign] = useState<RaspadinhaPublicCampaign | null>(null)
  const [step, setStep] = useState<RaspadinhaPublicStep>('welcome')

  const load = useCallback(async () => {
    if (!slug) {
      setError('Link inválido.')
      setCampaign(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setStep('welcome')

    try {
      const data = await fetchRaspadinhaPublicCampaign(slug)
      setCampaign(data)
    } catch (err) {
      const message =
        err instanceof RaspadinhaPublicCampaignUnavailableError
          ? err.message
          : err instanceof Error && err.message.trim()
            ? err.message
            : 'Não foi possível carregar a campanha. Tente novamente em instantes.'
      setError(message)
      setCampaign(null)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    void load()
  }, [load])

  const pageTitle = useMemo(() => {
    if (!campaign) return 'Campanha raspadinha'
    return pickPageTitle(campaign)
  }, [campaign])

  useEffect(() => {
    document.title = pageTitle
    return () => {
      document.title = 'Sortee'
    }
  }, [pageTitle])

  const shellStyle = campaign ? buildRaspadinhaPublicPageStyleVars(campaign) : undefined
  const isExampleCampaign = campaign?.slug === 'raspadinha-garfo-inox'

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={styles.statePanel} role="status" aria-live="polite">
          <Icon className={styles.stateIcon} icon="mdi:loading" aria-hidden />
          <p className={styles.stateText}>Carregando campanha...</p>
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className={styles.shell}>
        <div className={styles.statePanel} role="alert">
          <Icon className={styles.stateIconError} icon="mdi:alert-circle-outline" aria-hidden />
          <p className={styles.stateText}>{error ?? 'Campanha raspadinha não encontrada.'}</p>
          <button type="button" className={styles.stateRetryButton} onClick={() => void load()}>
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${styles.shell} ${step === 'experience' ? styles.shellExperience : ''}`}
      style={shellStyle}
    >
      <RaspadinhaPublicPixels fbPixel={campaign.fb_pixel} gPixel={campaign.g_pixel} />

      {isExampleCampaign && step !== 'experience' ? (
        <div className={styles.exampleBanner} role="status">
          Campanha de teste — use apenas para conhecer a experiência.
        </div>
      ) : null}

      <main
        className={`${styles.main} ${step === 'experience' ? styles.mainExperience : ''}`}
      >
        {step === 'welcome' ? (
          <RaspadinhaPublicWelcomeStep
            campaign={campaign}
            onContinue={() => setStep('register')}
          />
        ) : null}

        {step === 'register' ? (
          <RaspadinhaPublicRegisterStep
            campaign={campaign}
            onRegistered={() => setStep('receipt')}
          />
        ) : null}

        {step === 'receipt' ? (
          <RaspadinhaPublicReceiptStep campaign={campaign} onReady={() => setStep('experience')} />
        ) : null}

        {step === 'experience' ? <RaspadinhaPublicScratchStep campaign={campaign} /> : null}
      </main>
    </div>
  )
}
